/*
 * @Description: ^_^
 * @Author: sharebravery
 */
import type { Ref, VNode } from 'vue';
import { computed, createVNode, shallowRef, triggerRef, watch } from 'vue';
import type { SelectProps } from 'ant-design-vue/lib/select';
import type { OptionNode, OptionsItem } from '../types';

export function findLabelByValueForDisplay(
  attrs: SelectProps & Record<string, any>,
  getOptions: Ref<OptionsItem<unknown>[]>,
  slots: any,
  props: any
) {
  const optionNodes = shallowRef<OptionNode[]>([]);

  function getOptionNode(selectedNode: Omit<OptionNode, 'node'>): OptionNode {
    const node = optionNodes.value.find(v => v.value === selectedNode.value);
    return (
      node || {
        value: selectedNode.value,
        node: createVNode('span', null, selectedNode.label),
        label: selectedNode.label,
        data: selectedNode?.data,
      }
    );
  }

  function setOptionNodes(options: OptionsItem<unknown>[]) {
    optionNodes.value = [];
    optionNodes.value = options.map(e => {
      return {
        value: e.value,
        node: createVNode('span', null, e.label),
        label: e.label,
        data: e?.data,
      };
    });
    triggerRef(optionNodes);
  }

  const selectedNodes = computed(() => {
    const res: OptionNode[] = [];
    const { value, mode } = attrs;

    if (mode && ['tags', 'multiple'].includes(mode) && typeof value === 'object') {
      if (props.labelInValue) {
        (value as Array<any>)?.forEach(e => {
          res.push(getOptionNode(e));
        });
      } else {
        (value as Array<any>)?.forEach(e => {
          res.push(
            getOptionNode({
              value: e,
              label: e,
              data: null,
            })
          );
        });
      }
    } else if (props.labelInValue) {
      res.push(
        getOptionNode({
          value: (value as any)?.key ?? (value as any)?.value,
          label: (value as any)?.label,
          data: (value as any)?.data,
        })
      );
    } else if (['string', 'number'].includes(typeof value)) {
      res.push(
        getOptionNode({
          value: value as any,
          label: value as any,
          data: null,
        })
      );
    }

    return res;
  });

  function init() {
    if ('default' in slots) {
      optionNodes.value = [];
      findChildOption(slots.default(), optionNodes.value);
    } else if (getOptions.value?.length > 0) {
      setOptionNodes(getOptions.value);
    }
    triggerRef(optionNodes);
  }

  init();

  watch(() => slots, init);

  watch(() => getOptions.value, init, { immediate: true, deep: true });

  return { optionNodes, selectedNodes, setOptionNode: setOptionNodes };
}

export default ({ selectedNodes }: { selectedNodes: { key: any; node: VNode | string }[] }, context: any) => {
  const length = selectedNodes?.length - 1;
  return selectedNodes?.map((e, index) => (
    <span>
      {e.node}
      {index < length ? '、' : ''}
    </span>
  ));
};

function findChildOption(node: VNode[], optionNodes: OptionNode[]) {
  node.forEach(e => {
    if (typeof e.type == 'function') {
      if (e.type.name.includes('Option')) {
        const v = e.props?.value ?? e.props?.key;
        optionNodes.push({
          value: v,
          label: e.props?.label ?? v,
          data: null,
          node: (e.children as any)?.default(),
        });
      } else if (e.type.name.includes('OptGroup') && (e?.children as any)?.default) {
        findChildOption((e.children as any).default(), optionNodes);
      }
    }
    if (Array.isArray(e.children)) {
      findChildOption(e.children as VNode[], optionNodes);
    }
  });
}
